import { addDays } from "date-fns"
import { createContext, useContext, useMemo } from "react"
import { parseSettings, type IBuy, type ISupplyPlan } from "~/hooks/useSupplyPlan"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { useSafeLocalStorage } from "~/hooks/useSafeLocalStorage"
import { z } from "zod"

type NoosSupplyPlanDetailContextType = {
  supplyPlan: ISupplyPlan
  nextBuyDate: Date
  buys: IBuy[] | undefined
  /** Units/Cost display mode, shared by the plan's tabs and persisted per plan. */
  showCost: boolean
  setShowCost: (showCost: boolean) => void
}

const NoosSupplyPlanDetailContext = createContext<NoosSupplyPlanDetailContextType | null>(null)

// `isScoped` buys are off-cadence "immediate replenishments" created from the stockout alerts, so
// neither of these treats them as the plan's cadence. Letting one become the next buy date pulled
// the overview's whole coverage window back to the day that buy was placed, which dropped most of
// the stockout alerts the planner was working through (SLIM-576).
export function getCurrentBuyDateMs(buys: IBuy[] | undefined): number | null {
  const actionable = (buys ?? []).filter(
    (b) => !b.archived && !b.isScoped && b.buyDate != null && b.finalizedAt == null,
  )
  if (actionable.length === 0) return null
  return Math.min(...actionable.map((b) => new Date(b.buyDate!).getTime()))
}

export function getFallbackNextBuyDate(
  buys: IBuy[] | undefined,
  purchaseInterval: number,
  today: Date,
): Date {
  const relevant = (buys ?? []).filter((b) => !b.archived && !b.isScoped && b.buyDate != null)
  if (relevant.length === 0) return addDays(today, purchaseInterval)

  const mostRecentMs = Math.max(...relevant.map((b) => new Date(b.buyDate!).getTime()))
  let nextDate = addDays(new Date(mostRecentMs), purchaseInterval)
  // Advance by purchaseInterval until we reach a future date. Prevents a past nextBuyDate
  // from causing the suggestion tile to calculate with a stale/past order window.
  while (nextDate <= today) {
    nextDate = addDays(nextDate, purchaseInterval)
  }
  return nextDate
}

export function NoosSupplyPlanDetailProvider({
  children,
  supplyPlan,
}: {
  children: React.ReactNode
  supplyPlan: ISupplyPlan
}) {
  const purchaseInterval = parseSettings(supplyPlan.settings).purchaseInterval
  const today = useMemo(() => startOfTodayUTC(), [])

  const { data: buys } = api.supplyPlan.getBuysForSupplyPlan.useQuery({
    supplyPlanId: supplyPlan.id,
  })

  const currentBuyDateMs = useMemo(() => getCurrentBuyDateMs(buys), [buys])
  const defaultNextBuyMs = useMemo(
    () => currentBuyDateMs ?? getFallbackNextBuyDate(buys, purchaseInterval, today).getTime(),
    [currentBuyDateMs, buys, purchaseInterval, today],
  )

  const nextBuyDate = useMemo(() => new Date(defaultNextBuyMs), [defaultNextBuyMs])

  const [showCost, setShowCost] = useSafeLocalStorage(
    `noos-plan-show-cost-${supplyPlan.id}`,
    false,
    z.boolean(),
  )

  const value = useMemo(
    () => ({ supplyPlan, nextBuyDate, buys, showCost, setShowCost }),
    [supplyPlan, nextBuyDate, buys, showCost, setShowCost],
  )

  return (
    <NoosSupplyPlanDetailContext.Provider value={value}>
      {children}
    </NoosSupplyPlanDetailContext.Provider>
  )
}

export function useNoosSupplyPlanDetailContext() {
  const ctx = useContext(NoosSupplyPlanDetailContext)
  if (!ctx)
    throw new Error(
      "useNoosSupplyPlanDetailContext must be used within NoosSupplyPlanDetailProvider",
    )
  return ctx
}
